import { useEffect, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  myBrandDrawerPerformanceDataQueryOptions,
  type DrilldownPageFiltersInput,
  type DrilldownQuerySection,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import type { MyBrandCreativeCardModel } from "../lib/drilldown-card.types"
import {
  myBrandDrawerPerformanceCacheKey,
  peekCachedMyBrandDrawerPerformanceData,
  setCachedMyBrandDrawerPerformanceData,
  touchCachedMyBrandDrawerPerformanceData,
} from "./useMyBrandDrawerPerformanceDataCache"

export function useMyBrandDrawerPerformanceDataSection(
  creative: MyBrandCreativeCardModel | null,
  open: boolean,
  activeTab: string,
  myBrandFilters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
): {
  section: DrilldownQuerySection | null
  isLoading: boolean
  isError: boolean
} {
  const hash = creative?.hash ?? ""
  const adId = creative?.adId ?? null
  const cacheKey =
    hash ? myBrandDrawerPerformanceCacheKey(hash, adId, myBrandFilters, drilldownFilters) : ""
  const cached = useMemo(
    () => (cacheKey ? peekCachedMyBrandDrawerPerformanceData(cacheKey) : undefined),
    [cacheKey],
  )

  useEffect(() => {
    if (cacheKey && cached) touchCachedMyBrandDrawerPerformanceData(cacheKey)
  }, [cacheKey, cached])

  const shouldFetch = open && activeTab === "performance" && !!cacheKey && !cached

  const { data, isLoading, isError } = useQuery(
    myBrandDrawerPerformanceDataQueryOptions(
      hash,
      adId,
      myBrandFilters,
      drilldownFilters,
      shouldFetch,
    ),
  )

  useEffect(() => {
    if (data && cacheKey) setCachedMyBrandDrawerPerformanceData(cacheKey, data)
  }, [data, cacheKey])

  const section = cached ?? data ?? null

  return { section, isLoading: shouldFetch && isLoading, isError }
}
